import { BookOpen } from "lucide-react";
import type React from "react";
import cashforecasterIcon from "@/assets/cashforecaster.svg";
import { FeatureFeedback } from "@/components/FeatureFeedback";
import { UserAvatar } from "@/components/ui/user-avatar";
import { useMode } from "@/providers/ModeProvider";
import { useApp } from "../../contexts/AppContext";
import { useUserPreferences } from "../../contexts/UserPreferencesContext";
import { DEMO_USERS } from "../../contexts/UsersContext";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Switch } from "../ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

const FEEDBACK_FEATURE_MAP: Record<string, string> = {
  transactions: "Synced Transactions",
  "recurring-transactions": "Recurring Commitments",
  scenarios: "Scenarios",
};

interface HeaderProps {
  pageTitle?: string;
  currentPage?: string;
  onUserChange?: (userId: string) => void;
  onHelpOpen?: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  pageTitle,
  currentPage,
  onUserChange,
  onHelpOpen,
}) => {
  const { currentUser, setCurrentUser, mode, setMode } = useApp();
  const { isDemoAllowed } = useMode();
  const { getUserPreferences } = useUserPreferences();
  const currentUserPrefs = getUserPreferences(currentUser.id);

  // Filter out the 'regional' user (Marcus Patel) — hidden R1 feature
  const availableUsers = DEMO_USERS.filter(
    (u) => u.forecasterRole !== "Regional Manager",
  );

  return (
    <header className="border-b bg-background sticky top-0 z-50">
      <div className="flex h-16 items-center px-4 gap-4">
        {/* Logo */}
        <div className="flex items-center gap-2 font-semibold text-lg">
          <img
            src={cashforecasterIcon}
            alt="Cash Forecaster"
            className="h-6 w-6"
          />
          <span>{pageTitle || "Cash Forecaster"}</span>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Feature Feedback — only for eligible pages */}
        {currentPage && FEEDBACK_FEATURE_MAP[currentPage] && (
          <FeatureFeedback feature={FEEDBACK_FEATURE_MAP[currentPage]} />
        )}

        {/* Help Button */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onHelpOpen?.()}
                className="gap-1.5"
              >
                <BookOpen className="h-4 w-4" />
                Help
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              Get help with this page
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* User Switcher - Only visible in Demo Mode */}
        {mode === "demo" && (
          <div className="flex items-center gap-2">
            <Label
              htmlFor="user-select"
              className="text-sm text-muted-foreground"
            >
              Viewing as:
            </Label>
            <Select
              value={currentUser.id}
              onValueChange={(userId) => {
                const user = DEMO_USERS.find((u) => u.id === userId);
                if (user) {
                  setCurrentUser(user);
                  onUserChange?.(userId);
                }
              }}
            >
              <SelectTrigger id="user-select" className="w-[220px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableUsers.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name} ({u.forecasterRole})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Mode Toggle - Only visible when demo mode is allowed */}
        {isDemoAllowed && (
          <div className="flex items-center gap-2 border-l pl-4">
            <Switch
              id="mode-toggle"
              checked={mode === "live"}
              onCheckedChange={(checked) => setMode(checked ? "live" : "demo")}
            />
            <Label htmlFor="mode-toggle" className="text-sm">
              {mode === "live" ? (
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-green-500" />
                  Live Mode
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-orange-500" />
                  Demo Mode
                </span>
              )}
            </Label>
          </div>
        )}

        {/* Current user avatar */}
        <div className="border-l pl-4">
          <UserAvatar
            name={currentUser.name}
            avatarUrl={currentUserPrefs.avatarUrl}
            size="sm"
          />
        </div>
      </div>
    </header>
  );
};
